import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plug, RefreshCw, Unplug } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { getScenarioId } from '@/Config/auth/authStore';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { CopyField } from '@/Domain/components/Integration/components/CopyField';
import { IntegrationConnectDialog } from '@/Domain/components/Integration/components/IntegrationConnectDialog';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { ZendeskIcon } from '@/Domain/components/icons/Zendesk.icon';
import { useIntegrationConnectFunnel } from '@/Domain/hooks/Integration/useIntegrationConnectFunnel.hook';

interface ZendeskCredentials {
  app_id: string;
  api_key_id: string;
  api_key_secret: string;
}

interface ZendeskSettings {
  active: boolean;
  is_default: boolean;
  handoff: boolean;
}

type ZendeskIntegration = ZendeskCredentials & ZendeskSettings;

interface ZendeskSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY_CREDENTIALS: ZendeskCredentials = {
  app_id: '',
  api_key_id: '',
  api_key_secret: '',
};

const EMPTY_SETTINGS: ZendeskSettings = {
  active: false,
  is_default: false,
  handoff: false,
};

const isConnected = (data: unknown): data is ZendeskIntegration =>
  !!data && typeof data === 'object' && 'app_id' in data && !!(data as ZendeskIntegration).app_id;

export const ZendeskSheet = ({ open, onOpenChange }: ZendeskSheetProps) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const scenarioId = getScenarioId();
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);

  const queryKey = [QueryKey.integration.zendeskGet, scenarioId] as const;

  const { data } = useQuery<ZendeskIntegration | Record<string, never>>({
    queryKey,
    enabled: open && !!scenarioId,
  });

  const connected = isConnected(data);

  const { trackConnectStarted, trackConnectFailed } = useIntegrationConnectFunnel(
    'zendesk',
    connected,
  );

  const credentials: ZendeskCredentials = connected
    ? {
        app_id: data.app_id ?? '',
        api_key_id: data.api_key_id ?? '',
        api_key_secret: data.api_key_secret ?? '',
      }
    : EMPTY_CREDENTIALS;
  const settings: ZendeskSettings = connected
    ? {
        active: Boolean(data.active),
        is_default: Boolean(data.is_default),
        handoff: Boolean(data.handoff),
      }
    : EMPTY_SETTINGS;

  const tag = `quickchatai-bot-${scenarioId}`;

  const [form, setForm] = useState<ZendeskCredentials>(EMPTY_CREDENTIALS);

  // True while a reconnect is mid-flight (delete-then-create); stops the interim
  // disconnect from wiping the edited credentials and from toasting.
  const reconnectingRef = useRef(false);

  // When the sheet opens or fresh server data arrives, sync the form so the
  // disconnected view shows the (empty) credentials state and the connected
  // view shows the persisted ones.
  useEffect(() => {
    if (!open || reconnectingRef.current) return;
    setForm(connected ? credentials : EMPTY_CREDENTIALS);
    // credentials is derived from `data`; depending on `data` is sufficient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data]);

  const connectMutation = useMutation<
    ZendeskIntegration,
    unknown,
    { scenarioId: string; body: ZendeskCredentials }
  >({
    mutationKey: [QueryKey.integration.zendeskCreate],
    onSuccess: (responseData) => {
      queryClient.setQueryData(queryKey, responseData);
      setConnectDialogOpen(false);
      toast.success(t('integration:zendesk.connected'));
    },
    onError: () => {
      trackConnectFailed('connect_failed');
      toast.error(t('integration:zendesk.connect_failed'));
    },
  });

  const disconnectMutation = useMutation<unknown, unknown, { scenarioId: string }>({
    mutationKey: [QueryKey.integration.zendeskDelete],
    onSuccess: () => {
      queryClient.setQueryData(queryKey, {});
      // Within a reconnect this delete is just step one; only the connect result
      // is announced, so skip the interim "disconnected" toast.
      if (!reconnectingRef.current) {
        toast.success(t('integration:zendesk.disconnected'));
      }
    },
    onError: () => toast.error(t('integration:zendesk.disconnect_failed')),
  });

  const patchMutation = useMutation<
    ZendeskIntegration,
    unknown,
    { scenarioId: string; body: ZendeskSettings }
  >({
    mutationKey: [QueryKey.integration.zendeskPatch],
    onSuccess: (responseData) => {
      queryClient.setQueryData(queryKey, responseData);
    },
    onError: () => toast.error(t('integration:zendesk.update_failed')),
  });

  // The DELETE endpoint rejects active (and default) integrations, and
  // connecting now leaves the integration active — so deactivate + un-default
  // via PATCH first (same body as toggling Enable off), then delete.
  const deactivateThenDelete = useCallback(async (): Promise<void> => {
    if (settings.active || settings.is_default) {
      await patchMutation.mutateAsync({
        scenarioId,
        body: { active: false, is_default: false, handoff: settings.handoff },
      });
    }
    await disconnectMutation.mutateAsync({ scenarioId });
  }, [settings, patchMutation, disconnectMutation, scenarioId]);

  const handleConnect = useCallback(async () => {
    // Zendesk's create endpoint rejects when an integration already exists, so
    // reconnecting with edited credentials deletes the old one first, then
    // re-creates. On failure the dialog stays open with the edited form intact.
    if (connected) {
      reconnectingRef.current = true;
      try {
        await deactivateThenDelete();
        await connectMutation.mutateAsync({ scenarioId, body: form });
      } catch {
        // The mutations' onError handlers surface the failure.
      } finally {
        reconnectingRef.current = false;
      }
      return;
    }
    trackConnectStarted();
    connectMutation.mutate({ scenarioId, body: form });
  }, [connected, deactivateThenDelete, connectMutation, scenarioId, form, trackConnectStarted]);

  const handleDisconnect = useCallback(async () => {
    try {
      await deactivateThenDelete();
    } catch {
      // The mutations' onError handlers surface the failure.
    }
  }, [deactivateThenDelete]);

  const handleActiveToggle = useCallback(
    (checked: boolean) => {
      const next: ZendeskSettings = checked
        ? { ...settings, active: true }
        : { ...settings, active: false, is_default: false };
      const name = t('integration:zendesk.title');
      patchMutation.mutate(
        { scenarioId, body: next },
        {
          onSuccess: () => {
            toast.success(
              checked
                ? t('integrations:toast.enabled', { name })
                : t('integrations:toast.disabled', { name }),
            );
          },
        },
      );
    },
    [scenarioId, settings, patchMutation, t],
  );

  const handleSettingToggle = useCallback(
    (key: 'is_default' | 'handoff', checked: boolean) => {
      patchMutation.mutate({ scenarioId, body: { ...settings, [key]: checked } });
    },
    [scenarioId, settings, patchMutation],
  );

  const isSaving = connectMutation.isPending || disconnectMutation.isPending;
  const isFormValid = form.app_id.trim() && form.api_key_id.trim() && form.api_key_secret.trim();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0 space-y-0"
        data-testid="zendesk-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <ZendeskIcon className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="zendesk-sheet-title">
                  {t('integration:zendesk.title')}
                </SheetTitle>
                <IntegrationStatusBadge connected={connected} />
                <IntegrationEnabledBadge enabled={Boolean(settings.active)} />
              </div>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="zendesk-sheet-close-button"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6">
          <IntegrationIntroduction
            description={t('integration:zendesk.overview')}
            prerequisites={
              t('integration:zendesk.prerequisites', { returnObjects: true }) as string[]
            }
            docsHref="https://docs.quickchat.ai/channels/zendesk"
          />

          <IntegrationSection
            title={t('integrations:section.connect')}
            data-testid="zendesk-connect-enable-section"
          >
            {connected ? (
              <IntegrationConnectionCard
                data-testid="zendesk-connection-card"
                details={[
                  {
                    label: t('integration:zendesk.app_id_label'),
                    value: credentials.app_id,
                    'data-testid': 'zendesk-app-id-value',
                  },
                  {
                    label: t('integration:zendesk.api_key_id_label'),
                    value: credentials.api_key_id,
                    'data-testid': 'zendesk-api-key-id-value',
                  },
                ]}
                actions={
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => setConnectDialogOpen(true)}
                      data-testid="zendesk-reconnect-button"
                    >
                      <RefreshCw className="size-4" />
                      {t('integrations:reconnect')}
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      className="flex-1"
                      onClick={handleDisconnect}
                      disabled={isSaving}
                      data-testid="zendesk-disconnect-button"
                    >
                      <Unplug className="size-4" />
                      {disconnectMutation.isPending
                        ? t('integration:zendesk.disconnecting')
                        : t('integrations:disconnect')}
                    </Button>
                  </>
                }
              />
            ) : (
              <Button
                type="button"
                variant="dark"
                size="sm"
                className="w-full"
                onClick={() => setConnectDialogOpen(true)}
                data-testid="zendesk-connect-button"
              >
                <Plug className="size-4" />
                {t('integrations:connect', { name: 'Zendesk' })}
              </Button>
            )}
          </IntegrationSection>

          <IntegrationSection
            title={t('integrations:section.settings')}
            data-testid="zendesk-settings-section"
          >
            <IntegrationToggleField
              id="zendesk-active-toggle"
              label={t('integrations:enable', { name: 'Zendesk' })}
              checked={settings.active}
              onCheckedChange={handleActiveToggle}
              disabled={!connected}
              data-testid="zendesk-active-toggle"
            />

            <IntegrationToggleField
              id="zendesk-is-default-toggle"
              label={t('integration:zendesk.is_default_label')}
              tooltip={t('integration:zendesk.is_default_hint')}
              checked={settings.is_default}
              onCheckedChange={(checked) => handleSettingToggle('is_default', checked)}
              disabled={!connected || !settings.active}
              data-testid="zendesk-is-default-toggle"
            />

            <IntegrationToggleField
              id="zendesk-handoff-toggle"
              label={t('integration:zendesk.handoff_label')}
              tooltip={t('integration:zendesk.handoff_hint')}
              checked={settings.handoff}
              onCheckedChange={(checked) => handleSettingToggle('handoff', checked)}
              disabled={!connected || !settings.active}
              data-testid="zendesk-handoff-toggle"
            />
          </IntegrationSection>
        </div>

        <IntegrationConnectDialog
          open={connectDialogOpen}
          onOpenChange={setConnectDialogOpen}
          title={t('integrations:connect', { name: 'Zendesk' })}
          description={t('integration:zendesk.connect_description')}
          submitLabel={t('integrations:connect', { name: 'Zendesk' })}
          submitIcon={<Plug className="size-4" />}
          onSubmit={handleConnect}
          submitDisabled={!isFormValid || isSaving}
          isSubmitting={isSaving}
          data-testid="zendesk-connect-dialog"
        >
          <div className="space-y-1.5">
            <Label htmlFor="zendesk-app-id">{t('integration:zendesk.app_id_label')}</Label>
            <Input
              id="zendesk-app-id"
              data-testid="zendesk-app-id-input"
              placeholder={t('integration:zendesk.app_id_placeholder')}
              value={form.app_id}
              onChange={(e) => setForm({ ...form, app_id: e.target.value })}
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="zendesk-api-key-id">{t('integration:zendesk.api_key_id_label')}</Label>
            <Input
              id="zendesk-api-key-id"
              data-testid="zendesk-api-key-id-input"
              placeholder={t('integration:zendesk.api_key_id_placeholder')}
              value={form.api_key_id}
              onChange={(e) => setForm({ ...form, api_key_id: e.target.value })}
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="zendesk-api-key-secret">
              {t('integration:zendesk.api_key_secret_label')}
            </Label>
            <Input
              id="zendesk-api-key-secret"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-testid="zendesk-api-key-secret-input"
              placeholder={t('integration:zendesk.api_key_secret_placeholder')}
              value={form.api_key_secret}
              onChange={(e) => setForm({ ...form, api_key_secret: e.target.value })}
              className="w-full"
            />
          </div>
          <CopyField
            label={t('integration:zendesk.tag_label')}
            tooltip={t('integration:zendesk.tag_hint')}
            value={tag}
            testId="zendesk-tag"
            copyTestId="zendesk-tag-copy-button"
          />
        </IntegrationConnectDialog>
      </SheetContent>
    </Sheet>
  );
};
